import React, { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { selectModalData } from '../../../../../redux/selectors/modal';
import { closeModal } from '../../../../../redux/slices/modal';
import KubitModal from '../../../../../components/KubitModal';
import {
  FieldError,
  FieldLabel,
  InputField,
  TextAreaField,
  VerticalField,
} from '../../../../../components/Form';
import {
  defaultUpsertTileState,
  IUpsertTileState,
  upsertTileSchema,
} from './types';
import { ModalActions } from '../../../../../components/KubitModal/KubitModalUI';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/Button';
import { ADD_TILE, UPDATE_TILE } from '../../../../../utils/apollo/tiles';
import {
  IAddTileMutation,
  IAddTileMutationVariables,
  ITileFieldsFragment,
  IUpdateTileMutation,
  IUpdateTileMutationVariables,
} from '../../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../../utils/types';
import { addTile, updateTile } from '../../../../../redux/slices/tiles';
import toastMessage from '../../../../../utils/toastMessage';
import IconPicker from '../../../../../components/Form/IconPicker';
import { IconName } from '../../../../../components/Icon';
import styled from 'styled-components';

// Type guard to guarantee that modalData is correct
function isTileModalData(modalData: any): modalData is ITileFieldsFragment {
  return modalData satisfies ITileFieldsFragment;
}

// Icon list for Icon picker
const ICON_LIST: Array<IconName> = [
  'arrow-up-right',
  'funnel',
  'significance',
  'path',
  'envelope',
  'slack',
  'trend-up',
  'chart-pie',
  'chart-bar-icon',
  'chart-donut',
  'chart-line-up',
  'chart-bar-horizontal',
  'dashboard',
  'arrow-right',
  'chat',
  'eye',
  'arrow-circle-right',
  'arrow-circle-up-right',
  'arrow-fat-right',
  'monitor',
  'arrow-square-out',
  'plus',
  'cursor',
  'chat-text',
  'chat-regular',
  'arrow-square-up-right',
  'browser',
  'check',
  'check-fat',
  'check-circle',
  'link-regular',
  'house-line',
  'lightning',
  'star',
  'files',
  'chart-line',
];

const StyledVerticalField = styled(VerticalField)`
  align-items: start;
`;

const UpsertTileModal = () => {
  const dispatch = useAppDispatch();
  const modalData = useAppSelector(selectModalData);
  const isUpdate = isTileModalData(modalData);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleNewTile = ({ tileAdd }: IAddTileMutation) => {
    const { entity, errors } = tileAdd;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);
    dispatch(addTile(entity));

    handleClose();

    toastMessage({
      message: 'A new tile has been created',
      type: 'info',
    });
  };

  const [handleCreateTile, { loading: tileCreating }] = useMutation<
    IAddTileMutation,
    IAddTileMutationVariables
  >(ADD_TILE, {
    onCompleted: handleNewTile,
  });

  const handleUpdatedTile = ({ tileUpdate }: IUpdateTileMutation) => {
    const { entity, errors } = tileUpdate;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);
    dispatch(updateTile(entity));

    handleClose();

    toastMessage({
      message: 'Tile has been updated',
      type: 'info',
    });
  };

  const [handleUpdateTile, { loading: tileUpdating }] = useMutation<
    IUpdateTileMutation,
    IUpdateTileMutationVariables
  >(UPDATE_TILE, {
    onCompleted: handleUpdatedTile,
  });

  const defaultValues = useMemo(() => {
    if (isUpdate) {
      const { title, description, url, icon } = modalData;

      return {
        title,
        description,
        url,
        icon: icon as IconName,
      };
    }

    return defaultUpsertTileState;
  }, [isUpdate, modalData]);

  const formMethods = useForm<IUpsertTileState>({
    resolver: zodResolver(upsertTileSchema),
    defaultValues,
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
  } = formMethods;

  const onSubmitForm = handleSubmit(async (formData) => {
    if (isUpdate) {
      const { id } = modalData;

      await handleUpdateTile({
        variables: {
          input: {
            ...formData,
            id,
          },
        },
      });
    } else {
      await handleCreateTile({
        variables: {
          input: formData,
        },
      });
    }

    reset(formData);
  });

  const actionDisabled = tileCreating || tileUpdating || isSubmitting;

  return (
    <KubitModal
      open
      maxWidth={438}
      onClose={handleClose}
      header={isUpdate ? 'Update Tile' : 'Create New Tile'}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmitForm}>
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <VerticalField>
                <FieldLabel>Title</FieldLabel>
                <InputField
                  {...field}
                  autoComplete="off"
                  placeholder={'Add title'}
                  hasError={Boolean(fieldState.error)}
                />
                {fieldState.error?.message && (
                  <FieldError
                    type="custom"
                    customError={fieldState.error.message}
                  />
                )}
              </VerticalField>
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <VerticalField>
                <FieldLabel>Description</FieldLabel>
                <TextAreaField
                  {...field}
                  autoComplete="off"
                  placeholder={'Add description'}
                  hasError={Boolean(fieldState.error)}
                  minRows={4}
                />
                {fieldState.error?.message && (
                  <FieldError
                    type="custom"
                    customError={fieldState.error.message}
                  />
                )}
              </VerticalField>
            )}
          />
          <Controller
            name="url"
            control={control}
            render={({ field, fieldState }) => (
              <VerticalField>
                <FieldLabel>Add Link</FieldLabel>
                <InputField
                  {...field}
                  autoComplete="off"
                  placeholder={'https://www.company.kubit.com/report/query'}
                  hasError={Boolean(fieldState.error)}
                />
                {fieldState.error?.message && (
                  <FieldError
                    type="custom"
                    customError={fieldState.error.message}
                  />
                )}
              </VerticalField>
            )}
          />
          <Controller
            name="icon"
            control={control}
            render={({ field }) => (
              <StyledVerticalField>
                <FieldLabel>Icon</FieldLabel>
                <IconPicker
                  value={field.value}
                  onChange={field.onChange}
                  icons={ICON_LIST}
                  customWidth="64px"
                />
              </StyledVerticalField>
            )}
          />
          <ModalActions>
            <SecondaryButton
              type="button"
              onClick={handleClose}
              disabled={actionDisabled}
              trackContext="tiles"
              trackAction="cancel"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              isGreyedOut={!isDirty}
              disabled={actionDisabled}
              trackContext="tiles"
              trackAction={isUpdate ? 'save' : 'create'}
            >
              {isUpdate ? 'Save' : 'Create'}
            </PrimaryButton>
          </ModalActions>
        </form>
      </FormProvider>
    </KubitModal>
  );
};

export default UpsertTileModal;
